import { apiFetch } from '../utils/apiClient';

/* ── Active Policies ── */

export interface ActivePolicyItem {
  StatusExpression: string;
  PolicyCount: number;
  TotalPremium: string;
  AvgPremium: string;
}

export interface ActivePoliciesResponse {
  data: ActivePolicyItem[];
}

export async function fetchActivePolicies(): Promise<ActivePoliciesResponse> {
  return apiFetch<ActivePoliciesResponse>(
    `${process.env.REACT_APP_MyAMP_Url}/api/ams/dashboard/active-policies`
  );
}

/* ── Agency Production — This Month ── */

export interface AgencyProductionItem {
  Agent: string;
  PoliciesWritten: number;
  WrittenPremium: string;
  NewBusinessPremium: string;
  RenewalPremium: string;
  CommissionEarned: string;
  UniqueClients: number;
  PendingCancels: number;
  ExpiringIn30Days: number;
}

export interface AgencyProductionResponse {
  data: AgencyProductionItem[];
}

export async function fetchAgencyProduction(): Promise<AgencyProductionResponse> {
  return apiFetch<AgencyProductionResponse>(
    `${process.env.REACT_APP_MyAMP_Url}/api/ams/dashboard/agency-production-this-month`
  );
}

/* ── Renewals Unquoted ── */

export interface RenewalsUnquotedItem {
  UrgencyWindow: string;
  UnquotedPolicies: number;
  PremiumAtRisk: string;
  AgentsResponsible: string;
}

export interface RenewalsUnquotedResponse {
  data: RenewalsUnquotedItem[];
}

export async function fetchRenewalsUnquoted(): Promise<RenewalsUnquotedResponse> {
  return apiFetch<RenewalsUnquotedResponse>(
    `${process.env.REACT_APP_MyAMP_Url}/api/ams/dashboard/renewals-unquoted`
  );
}

/* ── Revenue at Risk ── */

export interface RevenueAtRiskItem {
  CancelReason: string;
  PolicyCount: number;
  PremiumAtRisk: string;
  EarliestCancel: string;
  AffectedClients: string;
}

export interface RevenueAtRiskResponse {
  data: RevenueAtRiskItem[];
}

export async function fetchRevenueAtRisk(): Promise<RevenueAtRiskResponse> {
  return apiFetch<RevenueAtRiskResponse>(
    `${process.env.REACT_APP_MyAMP_Url}/api/ams/dashboard/revenue-at-risk`
  );
}

/* ── Renewals Unquoted — Book Line ── */

export interface RenewalsUnquotedBookLineItem {
  BookLine: string;
  UnquotedPolicies: number;
  PremiumAtRisk: string;
}

export interface RenewalsUnquotedBookLineResponse {
  data: RenewalsUnquotedBookLineItem[];
}

export async function fetchRenewalsUnquotedBookLine(): Promise<RenewalsUnquotedBookLineResponse> {
  return apiFetch<RenewalsUnquotedBookLineResponse>(
    `${process.env.REACT_APP_MyAMP_Url}/api/ams/dashboard/renewals-unquoted-book-line`
  );
}

/* ── Policy Count by Carrier (Premium Share) ── */

export interface PolicyCountByCarrierItem {
  Carrier: string;
  CarrierId: string;
  PolicyCount: number;
  TotalPremium: string;
  UniqueClients: number;
  BookSharePct: number;
}

export interface PolicyCountByCarrierResponse {
  data: PolicyCountByCarrierItem[];
}

export async function fetchPolicyCountByCarrier(): Promise<PolicyCountByCarrierResponse> {
  return apiFetch<PolicyCountByCarrierResponse>(
    `${process.env.REACT_APP_MyAMP_Url}/api/ams/dashboard/policy-count-by-carrier`
  );
}

/* ── Policy Count by Carrier Book Line (Carrier Insight) ── */

export interface PolicyCountByCarrierBookLineItem {
  Carrier: string;
  LineOfBusiness: string;
  BookLine: string;
  PolicyCount: number;
  TotalPremium: string;
}

export interface PolicyCountByCarrierBookLineResponse {
  data: PolicyCountByCarrierBookLineItem[];
}

export async function fetchPolicyCountByCarrierBookLine(): Promise<PolicyCountByCarrierBookLineResponse> {
  return apiFetch<PolicyCountByCarrierBookLineResponse>(
    `${process.env.REACT_APP_MyAMP_Url}/api/ams/dashboard/policy-count-by-carrier-book-line`
  );
}

/* ── Notifications ── */

export interface NotificationItem {
  title: string;
  message: string;
}

export async function fetchNotifications(): Promise<NotificationItem> {
  return apiFetch<NotificationItem>(
    `${process.env.REACT_APP_Auth_Url}/api/momentumamp/notifications`
  );
}

/* ── Expiring 30 Days by Carrier ── */

export interface Expiring30DaysItem {
  Carrier: string;
  ExpiringPolicies: number;
  PremiumAtRisk: string;
  Risk: string;
}

export interface Expiring30DaysResponse {
  data: Expiring30DaysItem[];
}

export async function fetchExpiring30DaysByCarrier(): Promise<Expiring30DaysResponse> {
  return apiFetch<Expiring30DaysResponse>(
    `${process.env.REACT_APP_MyAMP_Url}/api/ams/dashboard/expiring-30-days-by-carrier`
  );
}

/* ── Revenue at Risk — Book Line ── */

export interface RevenueAtRiskBookLineItem {
  BookLine: string;
  CancellingPolicies: number;
  PremiumAtRisk: number;
}

export interface RevenueAtRiskBookLineResponse {
  data: RevenueAtRiskBookLineItem[];
}

export async function fetchRevenueAtRiskBookLine(): Promise<RevenueAtRiskBookLineResponse> {
  return apiFetch<RevenueAtRiskBookLineResponse>(
    `${process.env.REACT_APP_MyAMP_Url}/api/ams/dashboard/revenue-at-risk-book-line`
  );
}

/* ── Agency Production — Book Line ── */

export interface AgencyProductionBookLineItem {
  BookLine: string;
  PoliciesWritten: number;
  WrittenPremium: string;
  NewBusinessPremium: string;
  RenewalPremium: string;
  CommissionEarned: string;
  UniqueClients: number;
  BookSharePct: number;
}

export interface AgencyProductionBookLineResponse {
  data: AgencyProductionBookLineItem[];
}

export async function fetchAgencyProductionBookLine(): Promise<AgencyProductionBookLineResponse> {
  return apiFetch<AgencyProductionBookLineResponse>(
    `${process.env.REACT_APP_MyAMP_Url}/api/ams/dashboard/agency-production-this-month-book-line`
  );
}

/* ── Book Line Breakdown ── */

export interface BookLineBreakdownItem {
  BookLine: string;
  PolicyCount: number;
  TotalPremium: string;
  BookSharePct: number;
}

export interface BookLineBreakdownResponse {
  data: BookLineBreakdownItem[];
}

export async function fetchBookLineBreakdown(line?: string): Promise<BookLineBreakdownResponse> {
  const url = line
    ? `${process.env.REACT_APP_MyAMP_Url}/api/ams/dashboard/book-line-breakdown?line=${encodeURIComponent(line)}`
    : `${process.env.REACT_APP_MyAMP_Url}/api/ams/dashboard/book-line-breakdown`;
  return apiFetch<BookLineBreakdownResponse>(url);
}
